import * as XLSX from 'xlsx';

/**
 * A private helper function to generate and download an Excel file.
 * This logic is consolidated from the former excelExport.js utility.
 * @param {Array<Object>} data - The array of JSON objects to export.
 * @param {Array<Object>} headers - An array of header objects [{ key: 'dataKey', label: 'Display Name', width: 15 }].
 * @param {string} sheetName - The name of the sheet in the Excel file.
 * @param {string} fileName - The name of the Excel file (e.g., 'report.xlsx').
 */
const _createAndDownloadExcel = (data, headers, sheetName, fileName) => {
  // Create a worksheet from JSON data, specifying the order of columns using header keys
  const ws = XLSX.utils.json_to_sheet(data, { header: headers.map(h => h.key) });

  // Set column widths (optional)
  const wscols = headers.map(h => ({ wch: h.width || 15 })); // Default width 15
  ws['!cols'] = wscols;

  // Set header row display names
  headers.forEach((header, index) => {
    const cellRef = XLSX.utils.encode_cell({ r: 0, c: index });
    if (ws[cellRef]) {
      ws[cellRef].v = header.label;
    }
  });

  // Create a new workbook and append the worksheet
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);

  // Write the workbook to a file and trigger download
  XLSX.writeFile(wb, fileName);
};


/**
 * Handles the export of MLM consultant/team member data to an Excel file.
 * @param {Array<Object>} consultantData - The array of consultant records from the API response.
 * @param {Object} selectedUser - The user for whom the report is generated (used for filename).
 * @returns {boolean} - True if the export was initiated successfully, false otherwise.
 */
export const exportMlmUserReportToExcel = (consultantData, selectedUser) => {
  if (!consultantData || !Array.isArray(consultantData)) {
    console.error("Missing or invalid consultant data for Excel export.");
    return false;
  }

  try {
    // Using selectedUser's ID for the filename to make it specific
    const fileName = `MLM_User_Report_${selectedUser.userId}_${new Date().toISOString().slice(0, 10)}.xlsx`;
    const sheetName = 'Team Members';

    // Define headers for the Excel file based on your sample JSON response
    const headers = [
      { key: 'consultantId', label: 'Consult ID', width: 15 }, { key: 'firstName', label: 'First Name', width: 20 }, { key: 'secondName', label: 'Last Name', width: 20 }, { key: 'level', label: 'Level', width: 10 }, { key: 'sponsorId', label: 'Sponsor ID', width: 15 }, { key: 'mobile', label: 'Mobile', width: 15 }, { key: 'email', label: 'Email', width: 25 }, { key: 'address', label: 'Address', width: 30 }, { key: 'city', label: 'City', width: 15 }, { key: 'director', label: 'Director', width: 15 }, { key: 'personalBP', label: 'Personal BP', width: 15 }, { key: 'personalBV', label: 'Personal BV', width: 15 }, { key: 'personalDiscount', label: 'Personal Discount', width: 18 }, { key: 'groupBP', label: 'Group BP', width: 15 }, { key: 'groupBV', label: 'Group BV', width: 15 }, { key: 'groupDiscount', label: 'Group Discount', width: 18 }, { key: 'lastBPDate', label: 'Last BP Date', width: 18 }, { key: 'recruitDate', label: 'Recruit Date', width: 18 }, { key: 'signUpDate', label: 'Sign Up Date', width: 18 },
    ];

    // Format date fields for better readability in Excel
    const formattedData = consultantData.map(item => ({
      ...item,
      lastBPDate: item.lastBPDate ? new Date(item.lastBPDate).toLocaleDateString() : '',
      recruitDate: item.recruitDate ? new Date(item.recruitDate).toLocaleDateString() : '',
      signUpDate: item.signUpDate ? new Date(item.signUpDate).toLocaleDateString() : '',
    }));

    _createAndDownloadExcel(formattedData, headers, sheetName, fileName);
    console.log("Excel report generated successfully.");
    return true;
  } catch (error) {
    console.error("Error generating Excel report:", error);
    return false;
  }
};